import { useEffect, useState, useCallback } from 'react';
import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';

interface MicrosoftAuthConfig {
  clientId: string;
  authority?: string;
  redirectUri?: string;
  scopes?: string[];
  onSuccess: (response: any) => void;
  onError: (error: any) => void;
}

interface MicrosoftAuthHook {
  isLoaded: boolean;
  signIn: () => void;
  signOut: () => void;
  error: string | null;
}

const useMicrosoftAuth = ({
  clientId,
  authority = 'https://login.microsoftonline.com/common',
  redirectUri = window.location.origin,
  scopes = ['User.Read'],
  onSuccess,
  onError,
}: MicrosoftAuthConfig): MicrosoftAuthHook => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [msalInstance, setMsalInstance] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);

  // Initialize MSAL
  useEffect(() => {
    if (!clientId || clientId === 'your-microsoft-client-id-here') {
      setError('Microsoft Client ID not configured. Please set REACT_APP_MICROSOFT_CLIENT_ID in your environment variables.');
      return;
    }

    try {
      const msalConfig = {
        auth: {
          clientId,
          authority,
          redirectUri,
        },
        cache: {
          cacheLocation: 'localStorage',
          storeAuthStateInCookie: false,
        },
      };

      const instance = new PublicClientApplication(msalConfig);

      // Initialize MSAL instance
      instance.initialize().then(() => {
        setMsalInstance(instance);
        setIsLoaded(true);
        setError(null);
      }).catch((err: any) => {
        console.error('MSAL initialization error:', err);
        setError('Failed to initialize Microsoft authentication');
        onError(err);
      });
    } catch (err: any) {
      console.error('MSAL setup error:', err);
      setError('Failed to setup Microsoft authentication');
      onError(err);
    }
  }, [clientId, authority, redirectUri, onError]);

  const signIn = useCallback(async () => {
    if (!msalInstance) {
      const error = new Error('MSAL not initialized');
      setError('Microsoft authentication not ready');
      onError(error);
      return;
    }

    try {
      const loginRequest = {
        scopes,
        prompt: 'select_account',
      };

      const response = await msalInstance.loginPopup(loginRequest);

      if (response && response.account) {
        // Use tokens from login response
        onSuccess({
          account: response.account,
          accessToken: response.accessToken,
          idToken: response.idToken,
          user: {
            id: response.account.localAccountId,
            name: response.account.name,
            email: response.account.username,
            provider: 'microsoft',
          },
        });
      }
    } catch (err: any) {
      console.error('Microsoft login error:', err);
      setError(err.message || 'Microsoft login failed');
      onError(err);
    }
  }, [msalInstance, scopes, onSuccess, onError]);

  const signOut = useCallback(async () => {
    if (!msalInstance) {
      return;
    }

    try {
      await msalInstance.logoutPopup();
    } catch (err: any) {
      console.error('Microsoft logout error:', err);
      setError(err.message || 'Microsoft logout failed');
      onError(err);
    }
  }, [msalInstance, onError]);

  return {
    isLoaded,
    signIn,
    signOut,
    error,
  };
};

export default useMicrosoftAuth;